import React, { useState, useCallback } from 'react';
import { Button, Typography, Box } from '@material-ui/core';
import { ArrowDropDown, CalendarToday, AmpStoriesOutlined, Event } from '@material-ui/icons';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { TooltipMenu } from '../../../../common/tooltip-menu';

interface ViewOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

interface ViewSelectorDropdownProps {
  currentView: 'single' | 'calendar' | 'bulk';
  onViewChange: (view: 'single' | 'calendar' | 'bulk') => void;
}

const VIEW_OPTIONS: ViewOption[] = [
  {
    value: 'single',
    label: 'Single',
    icon: <Event style={{ fontSize: '16px' }} />,
  },
  {
    value: 'bulk',
    label: 'Bulk',
    icon: <AmpStoriesOutlined style={{ fontSize: '16px' }} />,
  },
  {
    value: 'calendar',
    label: 'Calendar',
    icon: <CalendarToday style={{ fontSize: '16px' }} />,
  },
];

export const ViewSelectorDropdown: React.FC<ViewSelectorDropdownProps> = ({ currentView, onViewChange }) => {
  const { theme, isMobile, isMd } = useCampfireTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleViewSelect = useCallback(
    (view: 'single' | 'calendar' | 'bulk') => {
      onViewChange(view);
      handleMenuClose();
    },
    [onViewChange, handleMenuClose]
  );

  const currentOption = VIEW_OPTIONS.find((option) => option.value === currentView) || VIEW_OPTIONS[0];

  return (
    <Box
      style={{
        border: '1px solid #dcdcdc',
        borderRadius: '4px',
        marginRight: '10px',
        display: 'inline-flex',
        backgroundColor: '#fff',
      }}
    >
      <Button
        style={{
          backgroundColor: '#fff',
          color: theme.color.grey.neutral400,
          textTransform: 'none',
          padding: '7px 12px',
          minWidth: '45px',
          border: 'none',
          borderRadius: '4px 0 0 4px',
        }}
        onClick={handleMenuOpen}
      >
        {currentOption.icon}
        <Typography
          style={{
            marginLeft: '4px',
            fontWeight: 600,
            fontSize: '13px',
            lineHeight: '19px',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            display: isMobile || isMd ? 'none' : 'inherit',
          }}
        >
          {currentOption.label}
        </Typography>
      </Button>
      <Box
        style={{
          width: '1px',
          backgroundColor: '#dcdcdc',
          alignSelf: 'stretch',
        }}
      />
      <TooltipMenu
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        title={
          <Box display='flex' flexDirection='column' alignItems='flex-start' style={{ padding: '0.5rem' }}>
            {VIEW_OPTIONS.map((option) => (
              <Button
                key={option.value}
                onClick={() => handleViewSelect(option.value as 'single' | 'calendar' | 'bulk')}
                style={{
                  color: theme.alert.blue.extraLight,
                  display: 'flex',
                  padding: '0.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  alignItems: 'center',
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  minWidth: 'auto',
                  backgroundColor: option.value === currentView ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                }}
                startIcon={option.icon}
              >
                <Typography style={{ fontSize: '14px', fontWeight: option.value === currentView ? 600 : 500 }}>
                  {option.label}
                </Typography>
              </Button>
            ))}
          </Box>
        }
      >
        <Button
          size='small'
          style={{
            backgroundColor: '#fff',
            color: theme.color.grey.neutral400,
            minWidth: '32px',
            border: 'none',
            padding: '7px 4px',
            borderRadius: '0 4px 4px 0',
          }}
          onClick={handleMenuOpen}
        >
          <ArrowDropDown style={{ fontSize: '18px' }} />
        </Button>
      </TooltipMenu>
    </Box>
  );
};
